import { find, findAll, prefersReducedMotion } from "./dom";

/**
 * The map of the grounds: pairs each legend row with the numbered points on the photograph.
 *
 * <p>The points are links and their cards open on hover and focus, so the map works without this
 * script. What it adds is the part a phone needs: the frame shows about half the map at a time, so
 * "where is number 7" is a real question, and tapping the legend row answers it by panning the map
 * to that point and lighting it.</p>
 *
 * <p>The legend rows become buttons here rather than in the template, for the reason the rail's
 * controls are built in script: a control that does nothing without the bundle should not be in the
 * markup at all.</p>
 */

/** How long a point stays lit after the legend sends the eye to it. */
const LIT_MS = 1800;

/** Reads a legend marker as the points it covers: "7" is one, "1-6" is six. */
function membersOf(marker: string): string[] {
  const range = /^(\d+)\s*[-–—]\s*(\d+)$/.exec(marker);
  if (range === null) {
    return [marker];
  }

  const from = Number(range[1]);
  const to = Number(range[2]);
  if (!Number.isFinite(from) || !Number.isFinite(to) || to < from || to - from > 50) {
    return [marker];
  }

  const members: string[] = [];
  for (let n = from; n <= to; n++) {
    members.push(String(n));
  }
  return members;
}

export function initSiteMap(): void {
  const figure = find<HTMLElement>(".site-map");
  const frame = figure === null ? null : find<HTMLElement>(".site-map__frame", figure);
  if (figure === null || frame === null) {
    return;
  }

  const pins = findAll<HTMLLIElement>(".site-map__pin", figure);
  const rows = findAll<HTMLLIElement>(".site-map__key", figure);
  if (pins.length === 0 || rows.length === 0) {
    return;
  }

  const pinFor = new Map<string, HTMLLIElement>();
  for (const pin of pins) {
    const marker = pin.dataset["marker"];
    if (marker !== undefined) {
      pinFor.set(marker, pin);
    }
  }

  // Create a complete image only when its card is first opened.
  for (const pin of pins) {
    const slot = find<HTMLElement>(".site-map__slot", pin);
    if (slot === null) {
      continue;
    }

    const show = (): void => {
      if (!slot.isConnected || !slot.dataset["src"]) {
        return;
      }
      const shot = document.createElement("img");
      shot.className = "site-map__shot";
      shot.alt = "";
      shot.width = Number(slot.dataset["width"]);
      shot.height = Number(slot.dataset["height"]);
      shot.decoding = "async";
      shot.sizes = "52px";
      shot.srcset = slot.dataset["srcset"] ?? "";
      shot.src = slot.dataset["src"];
      slot.replaceWith(shot);
    };

    pin.addEventListener("pointerenter", show, { once: true });
    pin.addEventListener("focusin", show, { once: true });
  }

  const live = document.createElement("p");
  live.className = "sr-only";
  live.setAttribute("role", "status");
  live.setAttribute("aria-live", "polite");
  figure.append(live);

  let clearLit: number | undefined;

  const light = (targets: readonly HTMLLIElement[], row: HTMLLIElement | null, lit: boolean): void => {
    for (const pin of targets) {
      pin.classList.toggle("site-map__pin--lit", lit);
    }
    row?.classList.toggle("site-map__key--lit", lit);
  };

  /** Puts every point and row out, so two rows in a row cannot leave the first one lit. */
  const darken = (): void => {
    window.clearTimeout(clearLit);
    clearLit = undefined;
    for (const pin of pins) {
      pin.classList.remove("site-map__pin--lit");
    }
    for (const row of rows) {
      row.classList.remove("site-map__key--lit");
    }
  };

  for (const row of rows) {
    const marker = row.dataset["marker"];
    if (marker === undefined) {
      continue;
    }

    const targets = membersOf(marker)
      .map((m) => pinFor.get(m))
      .filter((pin): pin is HTMLLIElement => pin !== undefined);

    if (targets.length === 0) {
      continue;
    }

    // The row's own spans move inside a button, so the label needs no second copy.
    const button = document.createElement("button");
    button.type = "button";
    button.append(...row.childNodes);
    row.append(button);

    // The row's label is written across several lines of markup, so its text carries the
    // indentation with it. Collapsed here, once, rather than announced as it stands.
    const label = (button.textContent ?? marker).replace(/\s+/g, " ").trim();

    button.addEventListener("click", () => {
      darken();

      // The middle of the group and not its first point: "1-6 Sjöstugorna" covers six cabins spread
      // across half the picture, and panning to cabin 1 would leave five of them off the edge.
      // offsetLeft is measured against the plate, which is the points' positioning context.
      let left = Infinity;
      let right = -Infinity;
      for (const pin of targets) {
        left = Math.min(left, pin.offsetLeft);
        right = Math.max(right, pin.offsetLeft);
      }

      const behavior = prefersReducedMotion() ? "auto" : "smooth";
      frame.scrollTo({ left: (left + right) / 2 - frame.clientWidth / 2, behavior });
      // The frame only scrolls sideways, so the page still has to bring the map into view when the
      // reader clicked a row while the picture was above the fold.
      figure.scrollIntoView({ block: "nearest", behavior });
      light(targets, row, true);
      live.textContent = `${label} — markerad på kartan`;
      clearLit = window.setTimeout(() => {
        clearLit = undefined;
        light(targets, row, false);
      }, LIT_MS);
    });

    // Hovering either half lights the other, so the eye can go from a name to a place and back
    // without clicking anything.
    const preview = (on: boolean): void => {
      if (clearLit === undefined) {
        light(targets, row, on);
      }
    };
    button.addEventListener("pointerenter", () => preview(true));
    button.addEventListener("pointerleave", () => preview(false));
    button.addEventListener("focus", () => preview(true));
    button.addEventListener("blur", () => preview(false));

    for (const pin of targets) {
      pin.addEventListener("pointerenter", () => preview(true));
      pin.addEventListener("pointerleave", () => preview(false));
      pin.addEventListener("focusin", () => preview(true));
      pin.addEventListener("focusout", () => preview(false));
    }
  }
}
